'use client';

import { useRef, useCallback } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import Image from 'next/image';
import Link from 'next/link';
import type { Collection } from '@/lib/shopify/types';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';

interface SlideData {
  collection: Collection;
  tagline: string;
}

interface HeroSliderProps {
  collections: Collection[];
}

// Taglines for collections (can be moved to Shopify metafields later)
const collectionTaglines: Record<string, string> = {
  't-shirts': 'Comfort That Looks Good',
  'polo-shirts': 'Classic Style Refined',
  'hoodies': 'Warmth Meets Style',
  'workwear': 'Built For The Job',
  'default': 'Quality You Can Trust',
};

export default function HeroSlider({ collections }: HeroSliderProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const slideCountRef = useRef(0);

  // Prepare slides - duplicate if less than 4 collections
  const prepareSlides = (): SlideData[] => {
    if (collections.length === 0) return [];

    /* Filter out collections without images (e.g. Clearance) — remove .filter() to revert */
    let slides: SlideData[] = collections.filter((c) => c.image?.url).map((collection) => ({
      collection,
      tagline: collectionTaglines[collection.handle] || collectionTaglines['default'],
    }));

    // Duplicate slides for smooth infinite loop
    while (slides.length < 6) {
      slides = [...slides, ...slides].slice(0, 6);
    }

    return slides;
  };

  const slides = prepareSlides();

  // Handle speed variation - slow down after every 2-3 slides
  const handleSlideChange = useCallback(() => {
    if (!swiperRef.current?.autoplay) return;

    slideCountRef.current += 1;

    // Every 3 slides, slow down briefly then speed up
    if (slideCountRef.current % 3 === 0) {
      // Slow down
      swiperRef.current.autoplay.stop();
      swiperRef.current.params.autoplay = {
        ...(typeof swiperRef.current.params.autoplay === 'object' ? swiperRef.current.params.autoplay : {}),
        delay: 0,
      };
      if (swiperRef.current.params.speed) {
        swiperRef.current.params.speed = 12000; // Slower
      }
      swiperRef.current.autoplay.start();

      // Speed back up after a delay
      setTimeout(() => {
        if (swiperRef.current) {
          swiperRef.current.params.speed = 7000; // Normal speed
        }
      }, 12000);
    }
  }, []);

  if (slides.length === 0) {
    return null;
  }

  return (
    <section className="hero-slider">
      <Swiper
        modules={[Autoplay, Pagination, FreeMode]}
        spaceBetween={6}
        slidesPerView={1.15}
        centeredSlides={false}
        loop={true}
        freeMode={{
          enabled: true,
          momentum: false,
        }}
        speed={7000}
        autoplay={{
          delay: 0,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        pagination={{
          clickable: true,
          bulletClass: 'hero-bullet',
          bulletActiveClass: 'hero-bullet-active',
        }}
        breakpoints={{
          640: {
            slidesPerView: 1.5,
            spaceBetween: 6,
          },
          1024: {
            slidesPerView: 2.2,
            spaceBetween: 8,
          },
          1400: {
            slidesPerView: 2.4,
            spaceBetween: 8,
          },
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={handleSlideChange}
        className="hero-swiper"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={`${slide.collection.id}-${index}`}>
            <Link
              href={`/collections/${slide.collection.handle}`}
              className="block relative h-[calc(100vh-70px)] overflow-hidden group"
            >
              {/* Background Image */}
              {slide.collection.image && slide.collection.image.url ? (
                <Image
                  src={slide.collection.image.url}
                  alt={slide.collection.image.altText ?? slide.collection.title}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                  priority={index < 2}
                  sizes="(max-width: 640px) 90vw, (max-width: 1024px) 60vw, 45vw"
                />
              ) : (
                <div className="absolute inset-0 bg-gray-800" />
              )}

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 pb-20 lg:p-10 lg:pb-24">
                <div className="flex items-end gap-4">
                  <h2
                    className="text-xl lg:text-2xl xl:text-3xl font-bold uppercase tracking-tight leading-none"
                    style={{ color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
                  >
                    {slide.collection.title}
                  </h2>
                  <div className="hidden sm:flex items-center gap-3 mb-1 lg:mb-3">
                    <span className="text-white/60 text-2xl">|</span>
                    <span className="text-white/90 text-sm lg:text-base font-medium uppercase tracking-wide">
                      {slide.tagline}
                    </span>
                  </div>
                </div>

                {/* Mobile tagline */}
                <p className="sm:hidden text-white/80 text-sm mt-2 uppercase tracking-wide">
                  {slide.tagline}
                </p>
              </div>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Styles */}
      <style jsx global>{`
        .hero-slider {
          width: 100vw;
          margin-left: calc(-50vw + 50%);
          padding: 0;
          overflow: hidden;
          position: relative;
        }

        .hero-swiper {
          padding-bottom: 0 !important;
        }

        .hero-swiper .swiper-wrapper {
          transition-timing-function: linear !important;
        }

        .hero-swiper .swiper-pagination {
          bottom: 32px !important;
          left: 0;
          right: 0;
          width: 100%;
          z-index: 10;
        }

        .hero-bullet {
          display: inline-block;
          width: 32px;
          height: 3px;
          background: rgba(255, 255, 255, 0.5);
          margin: 0 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          border-radius: 0;
        }

        .hero-bullet-active {
          width: 56px;
          background: #fff;
        }

        @media (max-width: 640px) {
          .hero-swiper .swiper-pagination {
            bottom: 24px !important;
          }

          .hero-bullet {
            width: 20px;
            height: 2px;
            margin: 0 4px;
          }

          .hero-bullet-active {
            width: 36px;
          }
        }
      `}</style>
    </section>
  );
}
